import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { AgentLayout } from '../../../components/AgentLayout';
import api from '../../../api';
import { CaseCard } from './Dashboard';

const STATUS_FILTERS = [
  ['all', 'All'],
  ['new_overdue', 'New'],
  ['ptp_set', 'PTP'],
  ['partially_paid', 'Part Paid'],
  ['contact_attempted', 'Attempted'],
  ['escalated_supervisor', 'Escalated'],
];

export default function AgentRecoveryCases() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const initialStatus = search.get('status') || 'all';
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/recovery/cases', { params });
      setCases(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) =>
      (c.customerName || '').toLowerCase().includes(q) ||
      (c.loanAccountNumber || '').toLowerCase().includes(q) ||
      (c.clientId || '').toLowerCase().includes(q) ||
      (c.customerPhone || '').includes(q)
    );
  }, [cases, query]);

  return (
    <AgentLayout>
      <div className="au-page">
        <div className="au-greeting fu">
          <div className="greet-hi">My Cases</div>
          <div className="greet-dt">{filtered.length} case{filtered.length !== 1 ? 's' : ''}</div>
        </div>

        {/* Search */}
        <div className="fu-1" style={{ marginBottom: '10px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} color="var(--g3)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              className="fi"
              placeholder="Search name, loan #, phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: '34px' }}
            />
          </div>
        </div>

        {/* Status chips */}
        <div className="fu-2" style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '4px' }}>
          {STATUS_FILTERS.map(([v, l]) => (
            <button
              key={v}
              onClick={() => setStatusFilter(v)}
              style={{
                flexShrink: 0,
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '14px',
                border: '1px solid',
                borderColor: statusFilter === v ? 'var(--navy)' : 'var(--g2)',
                background: statusFilter === v ? 'var(--navy)' : '#fff',
                color: statusFilter === v ? '#fff' : 'var(--g4)',
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="empty-state fu">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state fu">
            <div className="empty-title">No cases</div>
            <div className="empty-text">No recovery cases match your filters.</div>
          </div>
        ) : filtered.map((c, i) => (
          <CaseCard key={c.id} c={c} idx={i} onOpen={() => navigate(`/recovery/cases/${encodeURIComponent(c.loanAccountNumber)}`)} />
        ))}

        <div style={{ height: '20px' }} />
      </div>
    </AgentLayout>
  );
}
